import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Skeleton } from '@/components/ui/feedback/skeleton';
import { log } from '@/lib/logger';

interface RequireAuthProps {
  children: ReactNode;
}

/**
 * RequireAuth component that protects routes requiring authentication.
 * Uses AuthContext (backend-driven) instead of localStorage checks.
 *
 * - Shows loading skeleton while checking auth state
 * - Redirects to /login if user is not authenticated
 * - Redirects to /onboarding if onboarding is not complete
 * - Renders children if user is authenticated and onboarding is complete
 */
export function RequireAuth({ children }: RequireAuthProps) {
  const { user, loading, status, authReady } = useAuth();

  log.debug("AUTH", "Auth state:", { user, loading, status, authReady, hasUser: !!user, onboardingComplete: user?.onboarding_complete });

  // CRITICAL: Do NOT redirect while bootstrapping or loading
  // Show loading spinner until auth hydration completes
  // This prevents redirect loops on refresh/deep-link
  // Use authReady to ensure we never redirect during bootstrap
  if (!authReady || status === "bootstrapping" || loading) {
    log.debug("AUTH", "Bootstrapping, showing spinner");
    return (
      <div className="min-h-[100svh] flex items-center justify-center">
        <div className="space-y-4 w-full max-w-md p-8">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-8 w-3/4" />
        </div>
      </div>
    );
  }

  // CRITICAL: Only redirect after bootstrapping completes
  // Only check status, not user - status is the source of truth
  // Checking !user can cause race conditions during hydration
  if (status === "unauthenticated") {
    log.debug("AUTH", "User not authenticated, redirecting to login");
    return <Navigate to="/login" replace />;
  }

  // CRITICAL: Backend is source of truth for onboarding completion
  // If onboarding is not complete, redirect to onboarding
  if (status === "authenticated" && user && !user.onboarding_complete) {
    log.debug("AUTH", "User authenticated but onboarding not complete, redirecting to onboarding");
    return <Navigate to="/onboarding" replace />;
  }

  // User is authenticated and onboarding is complete
  log.debug("AUTH", "User authenticated and onboarding complete, rendering children");
  return <>{children}</>;
}
